/**
 * Everything the register listens to for the whole signed-in session (T07-40, R21) — mounted once
 * in the layout so that screens opening later never change the topic set mid-sale:
 *
 * 1. hub `pos:register:<id>` → the POS queries the change concerns refetch ({@link posKeyMap});
 *    a `resync` / the tab back → {@link REGISTER_RESYNC_KEYS};
 * 2. inventory-service `inventory.changed:*` (this location) → {@link AvailabilityStore} (sell
 *    buttons of the cards on screen);
 * 3. hub `pos:register:<id>` (pickup orders of this shop, T14-15) + pos `sale.changed` → 受取予定
 *    (badge, chime, toast) — no `staff:orders` (T07-42);
 * 4. pos-service's own stream (`pos.events:*` — basket, holds, sales, 買取);
 * 5. the offline copy (snapshot) follows 2. + the hub's product changes while online (T07-42,
 *    {@link useSnapshotFeed}) — sign-in + every 5 minutes stay as the safety net.
 */
import { registerTopic } from "@tcg/realtime";
import { useRealtime } from "@tcg/realtime/react";
import { useRealtimeInvalidation } from "@tcg/realtime/query";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAuthState, useCan, usePos, useRegister } from "@/app/context";
import { useToast } from "@/components/Toast";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { readSetting } from "@/lib/db";
import { POS_TOPICS } from "@/lib/sse";

import { useAvailabilityFeed } from "./availability";
import { REGISTER_RESYNC_KEYS } from "./keys";
import { PICKUP_SOUND_SETTING, usePickupFeed } from "./pickups";
import { useSnapshotFeed } from "./snapshotFeed";

const ALL_POS_TOPICS = Object.values(POS_TOPICS);

/** `pos:register:<id>` of this tablet (null before pairing). */
export function useRegisterTopic(): string | null {
  const state = useAuthState();
  const device = state.kind === "locked" || state.kind === "unlocked" ? state.device : null;
  return registerTopic(device?.registerId ?? null);
}

export interface RegisterRealtime {
  /** Web orders waiting at the counter (0 without `orders.read`). */
  pickupsWaiting: number;
}

export function useRegisterRealtime(): RegisterRealtime {
  const { t } = useTranslation();
  const toast = useToast();
  const { db, sounds, realtime, realtimeScope } = usePos();
  const state = useAuthState();
  const register = useRegister();
  const unlocked = state.kind === "unlocked";
  const topic = useRegisterTopic();

  // the inventory stream is scoped to this register + its location before it opens
  const locationId = register.data?.register.locationId ?? null;
  const device = state.kind === "locked" || state.kind === "unlocked" ? state.device : null;
  realtimeScope.set(device?.registerId ?? null, locationId);

  // a new staff member signed in / the PIN token was renewed: the hub adopts it on the open stream
  const staffId = unlocked ? state.staff.id : null;
  useEffect(() => {
    realtime?.tokenChanged();
  }, [realtime, staffId]);

  // pos-service's stream: every topic for the session (one connection, never reopened mid-sale)
  useRealtime(ALL_POS_TOPICS, undefined, { enabled: unlocked });
  useRealtimeInvalidation(topic ? [topic] : [], {
    enabled: unlocked,
    resyncKeys: REGISTER_RESYNC_KEYS,
  });
  useAvailabilityFeed(unlocked);
  const online = useOnlineStatus();
  useSnapshotFeed({ enabled: unlocked && online, locationId, topic });

  const canReadOrders = useCan("orders.read");
  const canSell = useCan("pos.sell");
  const canList = canReadOrders && canSell;
  const chime = useLiveQuery(() => readSetting(db, PICKUP_SOUND_SETTING, true), [db]) ?? true;
  const onArrived = useCallback(
    (count: number) => {
      if (chime) {
        sounds.play("notify");
      }
      toast.show(t("pickup.arrived", { count }), "info");
    },
    [chime, sounds, t, toast],
  );
  const pickupsWaiting = usePickupFeed({
    enabled: unlocked && canList,
    topic,
    locationId,
    onArrived,
  });
  return { pickupsWaiting };
}
